import { CAUSE, classifyMediaError } from "./deviceCheckRules"
import { markMicrophoneGranted } from "./microphoneGrant"
import { watchPermissionPrompt } from "./permissionPrompt"
import { permissionState } from "./permissionState"

/**
 * Ouverture des périphériques côté navigateur.
 *
 * Séparé de `useDeviceCheck` parce que rien ici ne dépend d'Expo ni de React :
 * ce sont les règles d'ouverture d'un flux web, et la seule qui compte tient en
 * une phrase — **on n'ouvre jamais un flux sans que l'élève l'ait demandé**,
 * sauf quand le navigateur affirme que l'accès est déjà accordé, auquel cas
 * rien ne surgit à l'écran. Isolée, elle se teste sans monter de composant et
 * sans module natif.
 */

/**
 * Ouvre puis relâche immédiatement un flux : on ne garde jamais le périphérique. `prompt` : ce que la
 * question du navigateur demandera, pour son guidage — seulement pour une demande faite dans le geste ;
 * `null` pour une sonde qui ne peut pas la poser (l'accès déjà accordé, l'échec déjà tranché).
 */
async function probeWeb(constraints, prompt = null) {
    try {
        const request = navigator.mediaDevices.getUserMedia(constraints)
        const stream = await (prompt != null ? watchPermissionPrompt(prompt, request) : request)
        stream.getTracks().forEach((track) => track.stop())
        // La page a son micro : le live pourra l'ouvrir sans reposer la question.
        if (constraints.audio) markMicrophoneGranted()
        return CAUSE.GRANTED
    } catch (error) {
        return classifyMediaError(error)
    }
}

/**
 * Sonde un appareil, puis lève l'ambiguïté du refus.
 *
 * Fermer la fenêtre d'autorisation et refuser explicitement produisent la même
 * erreur. Quand le navigateur sait encore répondre « prompt », c'est que rien
 * n'a été refusé : on repropose le bouton, au lieu d'envoyer l'élève régler une
 * permission qu'il n'a jamais refusée.
 */
async function settleWeb(name, constraints, prompt = null) {
    const cause = await probeWeb(constraints, prompt)
    if (cause !== CAUSE.DENIED) return cause
    return (await permissionState(name)) === "prompt" ? CAUSE.NOT_ASKED : CAUSE.DENIED
}

/**
 * Deux régimes, selon que l'élève a demandé quelque chose ou non.
 *
 * **Passage silencieux** (`request: false`, au chargement et à chaque
 * re-vérification) : on n'ouvre que ce qui est déjà accordé. Auparavant ce
 * passage appelait `getUserMedia` dans tous les cas, donc la fenêtre du
 * navigateur surgissait dès l'ouverture de l'écran, sans un mot d'explication et
 * sans geste de l'élève — ce que Safari peut refuser d'office.
 *
 * **Passage demandé** (`request: true`, au clic sur « Autoriser ») : les deux
 * appareils sont demandés ensemble, pour n'afficher qu'un seul pop-up comme le
 * veut la spec. Ce n'est qu'en cas d'échec qu'on sonde séparément pour savoir
 * lequel a lâché — à ce stade la décision est prise, ces sondes ne redemandent rien.
 */
export async function checkWeb({ audioDeviceId, videoDeviceId, only, request = true } = {}) {
    // Quand l'élève a choisi un autre périphérique dans le sélecteur, on teste
    // celui-là et pas le choix par défaut du système.
    const audio = audioDeviceId ? { deviceId: { exact: audioDeviceId } } : true
    const video = videoDeviceId ? { deviceId: { exact: videoDeviceId } } : true

    /**
     * On n'ouvre un flux que dans deux cas : l'élève vient de le demander, ou
     * le navigateur affirme que l'accès est déjà accordé — auquel cas rien ne
     * surgit. Partout ailleurs on répond « pas encore demandé » et on laisse le
     * bouton faire son travail.
     *
     * Le « partout ailleurs » compte : sur un navigateur qui ne renseigne pas
     * l'état des permissions, ouvrir quand même ferait resurgir la fenêtre à
     * chaque re-vérification, donc toutes les cinq secondes.
     */
    const inspect = async (name, constraints) => {
        const known = await permissionState(name)
        if (known === "denied") return CAUSE.DENIED
        if (!request && known !== "granted") return CAUSE.NOT_ASKED
        // Suivie seulement si une question peut s'ouvrir : demandée, et l'accès pas déjà accordé.
        return settleWeb(name, constraints, request && known !== "granted" ? name : null)
    }

    // Re-vérification ciblée : n'ouvrir que l'appareil dont il est question.
    // Sonder les deux ensemble allumait la webcam pendant l'étape micro.
    if (only === "microphone") return { microphone: await inspect("microphone", { audio }) }
    if (only === "camera") return { camera: await inspect("camera", { video }) }

    if (!request) {
        const [camera, microphone] = await Promise.all([
            inspect("camera", { video }),
            inspect("microphone", { audio }),
        ])
        return { camera, microphone }
    }

    const both = await probeWeb({ audio, video }, "both")
    if (both === CAUSE.GRANTED) {
        return { camera: CAUSE.GRANTED, microphone: CAUSE.GRANTED }
    }
    const [camera, microphone] = await Promise.all([
        settleWeb("camera", { video }),
        settleWeb("microphone", { audio }),
    ])
    return { camera, microphone }
}
